import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

/**
 * Button — minimal accessible base button.
 *
 * Designed to work in both browser and Capacitor WebView (no DOM-only quirks).
 * Use Tailwind utilities for variant overrides; this component provides safe
 * defaults (focus-visible ring, disabled states, semantic typography).
 */
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual variant — maps to brand tokens defined in @draftedge/ui's Tailwind preset. */
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'dangerSolid';
  /** Size token. */
  size?: 'sm' | 'md' | 'lg';
  /** Full width button (mobile-friendly default). */
  block?: boolean;
  children: ReactNode;
}

// Neon accent carries BLACK text (Spotify-style) — never white on bright green.
// `danger` is a quiet tint for inline destructive actions; `dangerSolid` is
// reserved for the confirm button inside a destructive dialog.
const VARIANT_CLASSES: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary:
    'bg-accent text-ink-inverse shadow-[0_0_16px_var(--de-accent-glow)] hover:bg-accent/90 active:bg-accent/80 active:shadow-[0_0_10px_var(--de-accent-glow)]',
  secondary:
    'bg-surface-subtle text-ink border border-line/50 hover:bg-surface-raised hover:border-slate-600/60 active:bg-surface',
  ghost:
    'bg-transparent text-ink-muted hover:bg-surface-subtle/60 hover:text-ink active:bg-surface-subtle',
  danger:
    'bg-brand-danger/15 text-brand-danger hover:bg-brand-danger/25 active:bg-brand-danger/30',
  dangerSolid:
    'bg-brand-danger text-white hover:bg-brand-danger-600 active:bg-brand-danger-700',
};

const SIZE_CLASSES: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      block = false,
      className = '',
      type = 'button',
      children,
      ...rest
    },
    ref,
  ) => {
    const base =
      'inline-flex items-center justify-center gap-2 rounded-md font-semibold tracking-tight ' +
      'transition-colors duration-150 ease-out ' +
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface ' +
      'disabled:opacity-50 disabled:cursor-not-allowed select-none';

    const composed = [
      base,
      VARIANT_CLASSES[variant],
      SIZE_CLASSES[size],
      block ? 'w-full' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button ref={ref} type={type} className={composed} {...rest}>
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';
